import { Add, Edit, Payments } from '@mui/icons-material';
import { Box, Drawer, Grid, IconButton, Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { addItemToOrder } from '../../../api/order/addItemToOrder';
import { changeOrderItem } from '../../../api/order/changeOrderItem';
import { closeOrder } from '../../../api/order/closeOrder';
import { removeItemFromOrder } from '../../../api/order/removeItemFromOrder';
import { DishVariation, Order as OrderType, OrderItem } from '../../../apiTypes';
import PrimaryButton from '../../../components/PrimaryButton';
import { borberBoxShadow, boxShadow } from '../../../constants/styles';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import { canClose } from '../../../utils/canCloseOrder';
import { getPromotionsApplied } from '../../../utils/getPromotionsApplied';
import useMedia from '../../../utils/useMedia';
import OrderDeliveryBlock from '../Delivery/OrderDeliveryBlock';
import Menu from '../Menu/Menu';
import OrderItemsList from '../OrderItemList/OrderItemList';
import PayModal from '../PayModal/PayModal';
import PrroReceipts from '../Prro/PrroReceipts';
import EditCommentModal from './EditCommentModal';
import OrderSettings from './OrderSettings';
import TableOrderBlock from './TableOrderBlock';

type Props = {
  order: DbDocument<OrderType>;
};

const Order = ({ order }: Props) => {
  const { lang } = useLocale();
  const { isSm } = useMedia();
  const { dishesMap, worker, promotions, legalEntities, productsMap, semiProductsMap, selfOrderPointsMap } = useCafe();
  const { cafeSlug, cafe } = useAdmin();
  const [isMenuOpen, setIsMenuOpen] = useState(!order?.items);
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);

  const items = useMemo(() => sortBy(Object.values(order.items), 'addTime'), [order.items]);

  const canBeClosed = useMemo(() => canClose(order, true), [order]);

  const canEdit =
    (worker?.credentials.create_and_edit_own_orders && order.workerId === worker.id) ||
    worker?.credentials.edit_orders_of_other_employees;

  const promotionsApplied = useMemo(() => getPromotionsApplied(promotions, Object.values(items)), [items, promotions]);

  const handleAdd = useCallback(
    async (dishId: string, variation: DishVariation | null = null) => {
      const dish = dishesMap[dishId];

      if (worker && cafe && dish && !order.paid?.total) {
        await addItemToOrder({
          cafeSlug,
          order,
          dish,
          variation,
          worker,
          cafe,
          legalEntities,
          productsMap,
          semiProductsMap,
          selfOrderPointsMap,
        });
      }
    },
    [cafe, cafeSlug, dishesMap, legalEntities, order, productsMap, selfOrderPointsMap, semiProductsMap, worker]
  );

  const handleEditItem = useCallback(
    async (item: OrderItem) => {
      const dish = dishesMap[item.dishId];
      if (worker && dish) {
        await changeOrderItem({
          order,
          worker,
          item,
          cafeSlug,
          dish,
          productsMap,
          semiProductsMap,
          selfOrderPointsMap,
        });
      }
    },
    [cafeSlug, dishesMap, order, productsMap, semiProductsMap, worker, selfOrderPointsMap]
  );

  const handleRemoveItem = useCallback(
    async (orderItem: OrderItem) => {
      await removeItemFromOrder({
        order,
        cafeSlug,
        dish: dishesMap[orderItem.dishId],
        worker,
        orderItem,
        selfOrderPointsMap,
      });
    },
    [cafeSlug, dishesMap, order, worker, selfOrderPointsMap]
  );

  const handleCloseOrder = useCallback(async () => {
    await closeOrder({ order, cafeSlug });
  }, [cafeSlug, order]);

  return (
    <div data-testid={`order-${order.number}`}>
      <Grid container>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            bgcolor: (t) => t.palette.background.paper,
            boxShadow: borberBoxShadow,
            borderRadius: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: window.innerHeight - (isSm ? 220 : 252), overflow: 'auto' }}>
            {order.type === 'delivery' && <OrderDeliveryBlock order={order} />}

            {order.type === 'tableOrder' && order.status === 'ordered' && <TableOrderBlock order={order} />}

            {order.comment && (
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  m: 1,
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                data-testid="order-comment"
              >
                <Typography>{order.comment}</Typography>
                <IconButton onClick={() => setIsCommentOpen(true)}>
                  <Edit />
                </IconButton>
              </Box>
            )}

            {items.length === 0 && (
              <Typography sx={{ mt: 3 }} variant="subtitle1" align="center" color={(t) => t.palette.text.disabled}>
                {t.no_items_in_order[lang]}
              </Typography>
            )}

            <Box m={1}>
              <OrderItemsList
                items={items}
                onEditItem={handleEditItem}
                onRevoveItem={handleRemoveItem}
                canRemove={!order.paid?.total && worker?.credentials?.delete_items_in_order !== false}
                canEditWight={!order.paid?.total}
                promotionsApplied={promotionsApplied}
                fiscalized={!!order.prroReceipts}
              />

              {isSm && !order.paid && (
                <PrimaryButton
                  sx={{ ml: 4 }}
                  startIcon={<Add />}
                  variant={items.length === 0 ? 'outlined' : 'text'}
                  onClick={() => setIsMenuOpen(true)}
                >
                  {t.add[lang]}
                </PrimaryButton>
              )}

              {isSm && order.paid && <PrroReceipts order={order} />}
            </Box>
          </Box>

          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              p: 1,
              bgcolor: (t) => t.palette.background.paper,
              borderColor: (t) => t.palette.grey[300],
              borderStyle: 'solid',
              borderWidth: 0,
              borderTopWidth: 1,
              borderRadius: 1,
              boxShadow,
            }}
          >
            <OrderSettings order={order} items={items} canEdit={canEdit} promotionsApplied={promotionsApplied} />

            {!order.paid && (
              <PrimaryButton
                onClick={() => setIsPayOpen(true)}
                variant="contained"
                color="secondary"
                disabled={items.length === 0 || !canEdit || order.status === 'ordered'}
                startIcon={<Payments />}
              >
                {t.pay[lang]}
              </PrimaryButton>
            )}
            {!!order.paid && (
              <Typography color={(t) => t.palette.success.light} sx={{ mr: 2 }}>
                {t.paid[lang]}
              </Typography>
            )}

            {canBeClosed && (
              <PrimaryButton onClick={handleCloseOrder} variant="contained">
                {t.close[lang]}
              </PrimaryButton>
            )}
          </Box>
        </Grid>

        {!isSm && (
          <Grid
            item
            xs={12}
            sm={6}
            pl={3}
            sx={{ height: window.innerHeight - (isSm ? 170 : 194), overflow: 'auto', position: 'relative' }}
          >
            {!order.paid && <Menu onAdd={handleAdd} items={items} />}
            {order.paid && <PrroReceipts order={order} />}
          </Grid>
        )}
      </Grid>

      {isSm && (
        <Drawer anchor="bottom" open={isMenuOpen} onClose={() => setIsMenuOpen(false)} sx={{ zIndex: 1300 }}>
          <Box sx={{ height: window.innerHeight * 0.6 }}>
            {!order.paid && <Menu onAdd={handleAdd} items={items} />}
            {order.paid && <PrroReceipts order={order} />}
          </Box>
        </Drawer>
      )}

      {isPayOpen && (
        <PayModal onClose={() => setIsPayOpen(false)} order={order} promotionsApplied={promotionsApplied} />
      )}

      {isCommentOpen && <EditCommentModal onClose={() => setIsCommentOpen(false)} order={order} />}
    </div>
  );
};

export default Order;
